import http from "../../app/http";
import L from "../../leaflet-src";
import "../../leaflet.markercluster-src";
import broadcast from "../../app/broadcast";
import WarningsWindowView from "./ui/WarningsWindowView";
import WarningsWindowController from "./WarningsWindowController";
import CheckBoxView from "../../ui/CheckBoxView";
import store from "../../app/store";
import CBWarningsFilterController from "./CBWarningsFilterController";
import rootScope from "../../app/rootScope";


/**
 * Clase que representa al conjunto de alertas en el visor.
 * @class
 */
class WarningsGroup{

    /**
     * Constructor de la clase WarningsGroup.
     * @constructor
     * @param {Object} map - mapa en el que se mostrarán las alertas.
     */
    constructor(map){

        // Inicializa vistas y controladores de elementos gráficos

        // Ventana donde aparece la información de las alertas
        const warningsWindowElement = document.querySelector("#list-warnings");
        if (warningsWindowElement) {
            const warningsWindowView = new WarningsWindowView(warningsWindowElement);
            const warningsWindowController = new WarningsWindowController(warningsWindowView);
        }

        // Check para activar o desactivar las alertas con riesgo leve
        const cbSlightWarningsElement = document.querySelector("#checkSlightWarnings");
        if(cbSlightWarningsElement){
            const cbSlightWarningsView = new CheckBoxView (cbSlightWarningsElement);
            const cbSlightWarningsController = new CBWarningsFilterController(cbSlightWarningsView, 0);
        }

        // Check para activar o desactivar las alertas con riesgo moderado
        const cbModerateWarningsElement = document.querySelector("#checkModerateWarnings");
        if(cbModerateWarningsElement){
            const cbModerateWarningsView = new CheckBoxView (cbModerateWarningsElement);
            const cbModerateWarningsController = new CBWarningsFilterController(cbModerateWarningsView, 1);
        }

        // Check para activar o desactivar las alertas con riesgo importante
        const cbImportantWarningsElement = document.querySelector("#checkImportantWarnings");
        if(cbImportantWarningsElement){
            const cbImportantWarningsView = new CheckBoxView (cbImportantWarningsElement);
            const cbImportantWarningsController = new CBWarningsFilterController(cbImportantWarningsView, 2);
        }

        // Check para activar o desactivar las alertas con riesgo extremo
        const cbExtremeWarningsElement = document.querySelector("#checkExtremeWarnings");
        if(cbExtremeWarningsElement){
            const cbExtremeWarningsView = new CheckBoxView (cbExtremeWarningsElement);
            const cbExtremeWarningsController = new CBWarningsFilterController(cbExtremeWarningsView, 3);
        }

        this.map = map

        // Fecha que corresponde con el día de hoy
        const today = new Date();
        this.today = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0)


        // Fecha que corresponde con el timestamp seleccionado en la barra de tiempo
        const date = new Date(store.get("timestamp"));
        this.selectedday = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)

        // Filtros para los cuatro niveles de riesgo de alertas
        this.risks = [true, true, true, true];

        // Capa de clusteres de alertas
        this.layerMarkerGroup = [];

        this.enableEvents();


    }

    /**
     * Carga las alertas de un día en concreto para el timestamp
     *
     * @param {number} timestamp - Timestamp.
     */
    changeTimestamp(timestamp){
        const date = new Date(timestamp);
        const ts = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)

        if (ts !== this.selectedday) {
            this.selectedday = ts;
            this.load().then(()=>{broadcast.emit("redrawFinished")})
        }
        else
            broadcast.emit("redrawFinished");

    }

    /**
     * Filtra las alertas para un riesgo seleccionado
     * @param {number} risk - Nivel de riesgo a filtrar.
     * @param {boolean} enabled - Si se ven o no las alertas del riesgo risk.
     */
    filterWarnings(risk, enabled) {
        this.risks[risk] = enabled;
        this.load();
    }


    /**
     * Habilita los eventos asociados a la clase.
     * @private
     */
    enableEvents(){

        this.filterWarningsBound=this.filterWarnings.bind(this);
        broadcast.on("warningsfilter",this.filterWarningsBound);

        this.changeTimestampBound=this.changeTimestamp.bind(this);
        store.on("timestamp", this.changeTimestampBound)
    }

    /**
     * Deshabilita los eventos asociados a la clase.
     * @private
     */
    disableEvents(){
        store.off("warningsfilter",  this.filterWarningsBound)
        store.off("timestamp", this.changeTimestampBound)
    }



    /**
     * Carga las alertas que se muestran en el mapa.
     * @async
     */
    async load(){

        // Si no se ha cargado el objeto json de alertas, se carga
        if ( ! this.warnings ) {
            const response = await http.get(rootScope.alertsServer + '/world');
            if (response.status === 200)
                this.warnings = response.data.data.respuesta.alertas[0].world;
        }


        if (this.warnings) {

            // Indice del día para el timestamp seleccionado, donde 0 es hoy, 1 mañana, 2 pasado, etc
            const index = Math.floor((this.selectedday - this.today) / (1000 * 60 * 60 * 24));


            // Se elimina del mapa la capa de los clusteres de alertas
            if (this.layerMarkerGroup)
                this.map.removeLayer(this.layerMarkerGroup);

            // Si el día es 0 o mayor
            if (index >= 0) {

                // Capa de clusteres de alertas
                this.layerMarkerGroup = L.markerClusterGroup({
                    showCoverageOnHover: false,
                    clusterPane: "warningsPane",

                    iconCreateFunction: (cluster) => {

                        let clase = "";
                        // Se busca si el cluster contiene alguna alerta con riesgo extremo. Si la encuentra
                        // se le añade la clase CSS risk para que pintarla de rojo y parpadeante
                        for (const marker of cluster.getAllChildMarkers()) {
                            if (marker.feature.r === 3) {
                                clase = "risk";
                                break;
                            }
                        }

                        return L.divIcon({
                            className: 'cluster_iconos_hurr',
                            iconSize: [28, 28],
                            iconAnchor: L.point(8, 20),
                            html: `<span class="clustergroup ${clase}">${cluster.getAllChildMarkers().length}</span>`
                        });
                    }
                });


                // Clases css según el riesgo de la alerta
                const clases = ["leve", "moderado", "importante", "extremo"];

                // Se añaden las alertas a la capa de clusteres de alertas
                for (let warning of this.warnings.days[index].groups) {

                    // Solo se añaden las que tienen su filtro activo a true
                    if (this.risks[warning.r]) {
                        const clase = clases[warning.r];

                        const icon = L.divIcon({
                            className: 'custom-div-icon',
                            html: `<div class='marker-pin ${clase}'><svg width="158" height="158" viewBox="0 0 158 158" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path class='background' d="M148.62 121.13L91.66 22.48C89.06 17.97 84.4 15.28 79.2 15.28C74 15.28 69.34 17.97 66.74 22.48L9.77001 121.13C7.17001 125.64 7.17001 131.02 9.77001 135.52C12.37 140.03 17.03 142.72 22.23 142.72H136.15C141.35 142.72 146.01 140.03 148.61 135.52C151.21 131.01 151.21 125.63 148.61 121.13H148.62Z" fill="#FACB00"/>
                            <path d="M74.4 62.8601C74.4 60.6501 76.5 58.8501 79.1 58.8501C81.7 58.8501 83.8 60.6401 83.8 62.8601V102.85C83.8 105.06 81.7 106.86 79.1 106.86C76.5 106.86 74.4 105.07 74.4 102.85V62.8601Z" fill="white"/>
                            <path d="M79.1 123.92C81.8614 123.92 84.1 121.681 84.1 118.92C84.1 116.158 81.8614 113.92 79.1 113.92C76.3386 113.92 74.1 116.158 74.1 118.92C74.1 121.681 76.3386 123.92 79.1 123.92Z" fill="white"/>
                       </svg></div>`,
                            iconSize: [40, 40],
                            iconAnchor: [20, 20]
                        });

                        let marker = L.marker([warning.la, warning.lo], {icon: icon, pane: "warningsPane"})

                        marker.feature = warning;

                        // Si se pincha sobre la alerta se desplegará una ventana
                        marker.addEventListener("click", () => {
                            broadcast.emit("warningdetail", marker.feature.group, index, this.risks);
                        })

                        marker.addTo(this.layerMarkerGroup);
                    }
                }


                this.layerMarkerGroup.addTo(this.map);


            }
        }
    }

    /**
     * Muestra la capa de alertas en el mapa.
     */
    show(){
        this.map.addLayer(this.layerMarkerGroup);
    }

    /**
     * Oculta la capa de alertas en el mapa.
     */
    hide(){
        this.map.removeLayer(this.layerMarkerGroup);
    }

}


export default WarningsGroup;
